import { useMemo } from "react";
import { useAppSelector } from "@store/hooks";

// Define the keys for the side tabs in the Service Agreement Policy UI.
export type KeysForCustomerSideTabs = "CONTACT_SIDE_TAB" | "ADDRESS_SIDE_TAB";

// Type definition for the indexes of the side tabs.
type SideTabsIndexes = {
    [key in KeysForCustomerSideTabs]: number;
};

// Type definition for the state (validity) of each side tab.
type SideTabsStates = {
    [key in KeysForCustomerSideTabs]: boolean;
};

/**
 * Custom hook for managing the state and behavior of side tabs in the Service Agreement Customer UI.
 * This hook encapsulates logic for determining the current active tab, its validity, and handling specific
 * UI logic based on the type of service agreement.
 *
 * @returns {Object} An object containing various states and configurations related to the side tabs.
 */
export const useCarePlanCustomerSideTab = () => {
    // State selectors from the Redux store.
    const serviceAgreementCustomerTab = useAppSelector(({ ui }) => ui.CarePlanCustomerActiveSideTab)
    const asContact = useAppSelector(({ ui }) => ui.CarePlanCustomerContactAsContact)
    const asProperty = useAppSelector(({ ui }) => ui.CarePlanCustomerAddressAsProperty)
    const isContactValid = useAppSelector(({ state }) => state.CarePlanCustomerContactValid)
    const isAddressValid = useAppSelector(({ state }) => state.CarePlanCustomerAddressValid)

    // useMemo to optimize the computation of the state (validity) of each side tab.
    const sideTabsStates = useMemo<SideTabsStates>(() => ({
        CONTACT_SIDE_TAB: isContactValid || asContact,
        ADDRESS_SIDE_TAB: isAddressValid || asProperty
    }), [isContactValid, asContact, isAddressValid, asProperty]);

    // useMemo to optimize the computation of the side tabs' indexes.
    const sideTabsIndexes = useMemo<SideTabsIndexes>(() => ({
        CONTACT_SIDE_TAB: 0,
        ADDRESS_SIDE_TAB: 1
    }), []);

    // Return the computed states and configurations.
    return { serviceAgreementCustomerTab, sideTabsStates, sideTabsIndexes };
};
